import { Add, Close, HideImage, Remove } from "@mui/icons-material";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  InputBase,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import PropTypes from "prop-types";
import React from "react";
import theme from "../../utils/theme";

export default function CartOverlay({
  isOpen,
  onClose,
  selectedDevices,
  cartQuantities,
  setCartQuantities,
  devices,
  onPlaceOrder,
  onRemoveDevice,
}) {
  const incrementQuantity = (deviceId) => {
    setCartQuantities((prev) => ({
      ...prev,
      [deviceId]: (prev[deviceId] || 1) + 1,
    }));
  };

  const decrementQuantity = (deviceId) => {
    setCartQuantities((prev) => ({
      ...prev,
      [deviceId]: Math.max(1, (prev[deviceId] || 1) - 1),
    }));
  };

  const handleQuantityInput = (deviceId, value) => {
    const parsed = parseInt(value, 10);
    setCartQuantities((prev) => ({
      ...prev,
      [deviceId]: Number.isNaN(parsed) ? "" : parsed,
    }));
  };

  const handleQuantityBlur = (deviceId, value) => {
    const parsed = parseInt(value, 10);
    setCartQuantities((prev) => ({
      ...prev,
      [deviceId]: Number.isNaN(parsed) || parsed < 1 ? 1 : parsed,
    }));
  };

  const getTotal = () =>
    Object.keys(selectedDevices).reduce((total, deviceId) => {
      const device = devices.find((d) => d.id.toString() === deviceId);
      const price = device?.price || 0;
      const quantity = cartQuantities[deviceId] || 1;
      return total + price * quantity;
    }, 0);

  const selectedDevicesList = Object.keys(selectedDevices).map((deviceId) => {
    const device = devices.find((d) => d.id.toString() === deviceId);
    return { ...device, deviceId };
  });

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          maxWidth: "100vw",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: `1px solid ${grey[300]}`,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Shopping Cart
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 2,
          }}
        >
          {selectedDevicesList.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">Your cart is empty</Typography>
            </Box>
          ) : (
            selectedDevicesList.map((device) => {
              const quantity = cartQuantities[device.deviceId] || 0;
              const price = device.price || 0;
              const subtotal = price * quantity;

              return (
                <Box
                  key={device.deviceId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 2,
                    borderBottom: `1px solid ${grey[200]}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {device.image?.url ? (
                      <img
                        src={device.image.url}
                        alt={device.display_name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <HideImage sx={{ fontSize: 35, color: grey[400] }} />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, mr: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {device.make} {device.display_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "11px" }}
                    >
                      {device.deviceType}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: `1px solid ${grey[300]}`,
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => decrementQuantity(device.deviceId)}
                      disabled={quantity <= 1}
                      sx={{
                        p: 0.5,
                        backgroundColor: theme.colors.stelBlue,
                        color: "#fff",
                        borderRadius: 0,
                        "&:hover": {
                          backgroundColor: "#1F79E2",
                        },
                        "&:disabled": {
                          backgroundColor: grey[300],
                          color: grey[500],
                        },
                      }}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <InputBase
                      value={quantity}
                      onChange={(e) =>
                        handleQuantityInput(device.deviceId, e.target.value)
                      }
                      onBlur={(e) =>
                        handleQuantityBlur(device.deviceId, e.target.value)
                      }
                      inputProps={{ min: 1 }}
                      sx={{
                        width: "45px",
                        fontSize: "14px",
                        "& input": { textAlign: "center", p: 0 },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => incrementQuantity(device.deviceId)}
                      sx={{
                        p: 0.5,
                        backgroundColor: theme.colors.stelBlue,
                        color: "#fff",
                        borderRadius: 0,
                        "&:hover": {
                          backgroundColor: "#1F79E2",
                        },
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      minWidth: "70px",
                      textAlign: "right",
                      flexShrink: 0,
                      ml: 2,
                    }}
                  >
                    {price === 0 ? "-" : `$${subtotal.toFixed(2)}`}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => onRemoveDevice(device.deviceId)}
                    sx={{ p: 0.5, flexShrink: 0, ml: 1.5 }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>

        {selectedDevicesList.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${grey[300]}`,
              bgcolor: grey[50],
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Total:
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: theme.colors.stelBlue }}
              >
                ${getTotal().toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                onPlaceOrder();
                onClose();
              }}
              sx={{
                textTransform: "capitalize",
                backgroundColor: theme.colors.stelBlue,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "#1F79E2",
                },
              }}
            >
              Create Order
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

CartOverlay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDevices: PropTypes.objectOf(PropTypes.bool).isRequired,
  cartQuantities: PropTypes.objectOf(PropTypes.number).isRequired,
  setCartQuantities: PropTypes.func.isRequired,
  devices: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onPlaceOrder: PropTypes.func.isRequired,
  onRemoveDevice: PropTypes.func.isRequired,
};
